/*
  Kikirie purchase flows, A/B tested like the Charmlry customizers (sections/kk-flow.liquid).

  Arms: steps (guided steps), chat (chat with the kit guide), plain (the product page as it is).
  Assignment: weighted random on the first visit, stored in localStorage + cookie "kikirie_ab" (90 days).
  ?flow=steps|chat|plain forces an arm and stores it (QA: ab_source "override", events not sent).
  Step 5 test (extras, steps flow only): a = mystery prints first, blades row below; b = "Add spare blades?"
  question first, then mystery prints with a small envelope stack on the right. Stored in "kikirie_x5",
  ?x5=a|b forces it (ab_x5_source "override"). Orders carry ab_x5 / ab_x5_source while the test runs.
  Test off: everyone gets the default arm and nothing is tracked.

  Events, through Shopify.analytics.publish (payload always carries ab_flow and ab_source):
    ab_view          product page seen
    ab_open          the buy button tapped (all arms)
    ab_step          a step or chat question reached (flow_step, e.g. steps:gifts, chat:mystery)
    ab_add_to_cart   lines added (value, kits, gift_kits, mystery, blades, addons_value)
  The flow adds every line in one /cart/add.js call (gift kits carry "Gift for"), tags the cart with the
  attributes ab_flow / ab_source, and goes straight to checkout. "Every extra kit is 30% off" is the
  automatic Buy X Get Y discount in Shopify (SECOND_OFF below must match it); the flow only shows the same arithmetic.
*/
(() => {
  'use strict';
  if (window.KikirieFlow) return;
  const root = document.querySelector('[data-kk-flow]');
  if (!root) return;
  let cfg;
  try {
    cfg = JSON.parse(root.querySelector('[data-kk-flow-config]').textContent);
  } catch (err) {
    return;
  }
  const dialog = root.querySelector('dialog');
  const app = root.querySelector('[data-kkf-app]');
  if (!dialog || !app || !cfg.kits || !cfg.kits.length) return;

  /* ---------- assignment ---------- */
  const ARMS = ['steps', 'chat', 'plain'];
  const KEY = 'kikirie_ab';
  const days = Math.max(1, +cfg.cookieDays || 90);
  const weights = cfg.weights || {};
  const weightOf = (a) => Math.max(0, +weights[a] || 0);
  const readCookie = () => {
    const m = document.cookie.match(new RegExp('(?:^|; )' + KEY + '=([a-z]+)'));
    return m ? m[1] : null;
  };
  const writeCookie = (v) => {
    try {
      document.cookie = `${KEY}=${v}; Max-Age=${days * 86400}; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
    } catch (err) { /* ignore */ }
  };
  const stored = () => {
    let v = null;
    try { v = localStorage.getItem(KEY); } catch (err) { /* ignore */ }
    if (!ARMS.includes(v)) v = readCookie();
    return ARMS.includes(v) ? v : null;
  };
  const store = (v) => {
    try { localStorage.setItem(KEY, v); } catch (err) { /* ignore */ }
    writeCookie(v);
  };
  const draw = () => {
    const total = ARMS.reduce((t, a) => t + weightOf(a), 0);
    if (!total) return ARMS.includes(cfg.defaultArm) ? cfg.defaultArm : 'steps';
    let r = Math.random() * total;
    for (const a of ARMS) {
      r -= weightOf(a);
      if (r < 0) return a;
    }
    return ARMS[ARMS.length - 1];
  };
  let override = null;
  try {
    const q = new URLSearchParams(location.search).get('flow');
    if (ARMS.includes(q)) override = q;
  } catch (err) { /* ignore */ }

  let arm;
  let source;
  if (override) {
    arm = override;
    source = 'override';
    store(arm);
  } else if (!cfg.enabled) {
    arm = ARMS.includes(cfg.defaultArm) ? cfg.defaultArm : 'steps';
    source = 'off';
  } else {
    arm = stored();
    source = arm ? 'stored' : 'new';
    if (arm && !weightOf(arm)) {
      arm = draw(); // the stored arm was switched off since: draw again, once
      source = 'reassigned';
      store(arm);
    } else if (!arm) {
      arm = draw();
      store(arm);
    }
  }
  document.documentElement.setAttribute('data-kk-flow', arm);

  /* ---------- step 5 test (extras) ---------- */
  const X5 = ['a', 'b'];
  const X5_KEY = 'kikirie_x5';
  const xcfg = cfg.extrasTest || {};
  const xWeight = (v) => Math.max(0, +((xcfg.weights || {})[v]) || 0);
  let x5 = 'a';
  let x5Source = 'off';
  {
    let q = null;
    try { q = new URLSearchParams(location.search).get('x5'); } catch (err) { /* ignore */ }
    const readX = () => {
      let v = null;
      try { v = localStorage.getItem(X5_KEY); } catch (err) { /* ignore */ }
      if (!X5.includes(v)) { const m = document.cookie.match(/(?:^|; )kikirie_x5=([ab])/); v = m ? m[1] : null; }
      return X5.includes(v) ? v : null;
    };
    const storeX = (v) => {
      try { localStorage.setItem(X5_KEY, v); } catch (err) { /* ignore */ }
      try { document.cookie = `${X5_KEY}=${v}; Max-Age=${days * 86400}; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`; } catch (err) { /* ignore */ }
    };
    const drawX = () => { const t = xWeight('a') + xWeight('b'); return !t || Math.random() * t < xWeight('a') ? 'a' : 'b'; };
    if (X5.includes(q)) { x5 = q; x5Source = 'override'; storeX(x5); }
    else if (xcfg.enabled) {
      const s = readX();
      if (s && xWeight(s)) { x5 = s; x5Source = 'stored'; } else { x5 = drawX(); x5Source = s ? 'reassigned' : 'new'; storeX(x5); }
    }
  }
  const x5On = arm === 'steps' && x5Source !== 'off';
  document.documentElement.setAttribute('data-kk-x5', x5);

  /* ---------- tracking ---------- */
  const noTrack = !cfg.enabled || source === 'override' || !!(window.Shopify && window.Shopify.designMode) || !!navigator.webdriver;
  const queue = [];
  const flush = () => {
    if (!(window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function')) return false;
    while (queue.length) {
      const ev = queue.shift();
      try { Shopify.analytics.publish(ev[0], ev[1]); } catch (err) { /* ignore */ }
    }
    return true;
  };
  let tries = 0;
  const timer = setInterval(() => { if (flush() || ++tries > 40) clearInterval(timer); }, 250);
  const publish = (name, data) => {
    const payload = Object.assign({ ab_flow: arm, ab_source: source, product: cfg.product || '', page: location.pathname }, x5On ? { ab_x5: x5, ab_x5_source: x5Source } : {}, data || {});
    try { document.dispatchEvent(new CustomEvent('kikirie:ab', { detail: { name, data: payload } })); } catch (err) { /* ignore */ }
    if (noTrack) return;
    queue.push([name, payload]);
    flush();
  };
  let lastStep = null;
  const stepEvent = (step) => {
    if (step === lastStep) return;
    lastStep = step;
    publish('ab_step', { flow_step: step });
  };
  const nativeFetch = window.fetch.bind(window);
  // extra: more order attributes for the A/B dashboard (ab_who = gift | me | both, from the flows)
  const tagCart = async (extra) => {
    if (noTrack && source !== 'override') return; // override: tagged as such so QA orders can be left out
    try {
      await nativeFetch(cfg.routes.update + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: Object.assign({ ab_flow: arm, ab_source: source }, x5On ? { ab_x5: x5, ab_x5_source: x5Source } : {}, extra || {}) }),
      });
    } catch (err) { /* ignore */ }
  };

  /* ---------- data ---------- */
  const KITS = {};
  cfg.kits.forEach((k) => {
    if (k && k.key && k.available !== false) KITS[k.key] = k;
  });
  const ORDER = ['florals', 'animals', 'landscapes', 'collection'].filter((k) => KITS[k]).concat(Object.keys(KITS).filter((k) => !['florals', 'animals', 'landscapes', 'collection'].includes(k)));
  const THEMES = ORDER.filter((k) => k !== 'collection');
  if (!ORDER.length) return;
  const LEVELS = ['First cut', 'Easy', 'Intermediate', 'Master', 'Virtuoso'];
  const RELS = ['Mom', 'Dad', 'Partner', 'Sister', 'Brother', 'Friend', 'Grandma', 'Teacher'];
  const LINES = {
    florals: 'from three tulips to a wisteria window',
    animals: 'from a swallow pair to a heron in the reeds',
    landscapes: 'from a mountain sunrise to an old oak on a hill',
  };
  const money = (cents) => {
    const v = Math.round(cents) / 100;
    return '$' + (Number.isInteger(v) ? String(v) : v.toFixed(2));
  };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const kitName = (key) => (key === 'collection' ? 'The Collection' : `${KITS[key].short} kit`);
  const photo = (key) => (KITS[key] && KITS[key].photo) || '';
  const designsOf = (key) => (KITS[key] ? KITS[key].designs || [] : []).slice().sort((a, b) => a.l - b.l || a.p - b.p);
  const fmt = (n) => Number(n || 0).toLocaleString('en-US');
  const hardest = (key) => designsOf(key).slice().sort((a, b) => b.p - a.p)[0];
  const blades = cfg.blades;
  const mystery = cfg.mystery;

  /* ---------- state ---------- */
  const S = {
    step: 0,
    who: 'gift',
    mainFor: null,
    kit: null, // nothing preselected: the visitor picks a kit in step 1 (Paolo, Oct 18); the chat arm starts on the page's kit
    needKit: false, // "Next" tapped before a kit was picked
    people: [],
    mq: 0,
    shownMq: 0,
    blades: 0, // packs of spare blades: 0, 1 or 2 (the x2 chip doubles it)
    going: false,
    error: '',
    enter: true,
    seenPid: 0,
    zoom: null, // step 1 design zoom: "key|index"
    pick: null, // step 4 kit sheet: "new|Dad" or "id|3"
    c: { stage: 0, shown: 0, typing: false, kitPicked: null, exp: 'never', animStage: -1 },
  };
  if (arm === 'chat') S.kit = KITS[cfg.currentKit] ? cfg.currentKit : ORDER[0];
  let pid = 0;
  // promo countdown (sections/kk-promo.liquid): the coupon of the gifts step wears the promo kicker ("Halloween" above "Gift offer") and drops the paper pieces once per open
  const promoKick = () => {
    const k = window.KikiriePromo && window.KikiriePromo.kicker ? window.KikiriePromo.kicker() : '';
    return k ? `<span class="kf-coupon-kick">${window.KikiriePromo.moon || ''}${esc(k)}</span>` : '';
  };
  let promoFx4 = false;

  /* every extra kit is 30% off: the Shopify discounts "Extra kits 30% off · N kits" (2 to 10 kits) take the same flat amount
     (30% of the cheapest kit, $14.10) off for every kit after the first, The Collection included; the flow shows it on the added kits */
  const SECOND_OFF = 0.3;
  const offPrice = (cents) => Math.round(cents * (1 - SECOND_OFF));
  const order = () => {
    const kits = (S.kit ? [{ idx: -1, key: S.kit, who: S.mainFor ? `For ${S.mainFor}` : S.who === 'gift' ? 'Gift' : 'For you' }] : []).concat(
      S.people.map((p, i) => ({ idx: i, key: p.theme, who: `For ${p.label}`, label: p.label }))
    );
    kits.forEach((k) => { k.price = KITS[k.key].price; });
    const flat = Math.round(cheapest() * SECOND_OFF);
    kits.forEach((k) => { k.half = k.idx >= 0 && k.idx < 9; });
    let total = 0;
    let save = 0;
    let gifts = 0;
    kits.forEach((k) => {
      k.final = k.half ? k.price - Math.min(flat, k.price) : k.price;
      total += k.final;
      if (k.half) save += k.price - k.final;
      if (k.idx >= 0) gifts += k.final;
    });
    const mysteryValue = mystery ? S.mq * mystery.price : 0;
    const bladesValue = blades && S.blades ? blades.price * S.blades : 0;
    total += mysteryValue + bladesValue;
    return { kits, total, save, gifts, mysteryValue, bladesValue };
  };
  const cheapest = () => Math.min(...THEMES.map((k) => KITS[k].price));
  const extraPrice = (cents) => cents - Math.min(Math.round(cheapest() * SECOND_OFF), cents);

  const addPerson = (rel, theme) => {
    const same = S.people.filter((p) => p.rel === rel).length;
    const start = Math.max(0, THEMES.indexOf(S.kit));
    S.people.push({ id: ++pid, rel, label: same ? `${rel} ${same + 1}` : rel, theme: KITS[theme] ? theme : THEMES[(start + 1 + S.people.length) % THEMES.length] });
  };


  /* ---------- icons ---------- */
  const svg = (d, size = 20, extra = '') => `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"${extra}>${d}</svg>`;
  const I = {
    close: '<path d="M6 6l12 12M18 6L6 18"/>',
    back: '<path d="M19 12H5M11 6l-6 6 6 6"/>',
    arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>',
    check: '<path d="M5 12.5l4.5 4.5L19 7.5"/>',
    plus: '<path d="M12 5v14M5 12h14"/>',
    minus: '<path d="M5 12h14"/>',
    edit: '<path d="M4 20h4L19 9l-4-4L4 16v4z"/><path d="M13.5 6.5l4 4"/>',
    gift: '<rect x="3" y="8" width="18" height="13" rx="1"/><path d="M3 12h18M12 8v13M12 8c-1.5-3-5-4-5.5-2S9 8 12 8zm0 0c1.5-3 5-4 5.5-2S15 8 12 8z"/>',
    cal: '<rect x="3" y="5" width="18" height="16" rx="1"/><path d="M3 10h18M8 3v4M16 3v4"/>',
    bag: '<path d="M5 8h14l-1 12H6L5 8z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/>',
    sheet: '<rect x="5" y="3" width="14" height="18" rx="1"/><path d="M9 9c1-2 5-2 6 0-1 2-5 2-6 0zM12 11v6"/>',
    practice: '<rect x="5" y="3" width="14" height="18" rx="1"/><path d="M8 8h8M8 12c1.3-2 2.7-2 4 0s2.7 2 4 0M8 16h5" stroke-dasharray="2 2"/>',
    knife: '<path d="M3 21l11-11"/><path d="M14 10l2.5-6.5L20 7l-6 3z"/>',
    blades: '<rect x="3" y="8" width="18" height="8" rx="1"/><path d="M7 10v4M10 10v4M13 10v4M16 10v4"/>',
    mat: '<rect x="3" y="5" width="18" height="14" rx="1"/><path d="M3 10h18M3 14h18M9 5v14M15 5v14"/>',
    cards: '<rect x="8" y="3" width="12" height="15" rx="1"/><path d="M5 6v14a1 1 0 0 0 1 1h11"/>',
    guide: '<path d="M3 5h6a3 3 0 0 1 3 3v12a2 2 0 0 0-2-2H3zM21 5h-6a3 3 0 0 0-3 3v12a2 2 0 0 1 2-2h7z"/>',
  };
  const envelope = (cls, style) =>
    `<span class="kf-env ${cls}" style="${style}"><svg viewBox="0 0 64 44" aria-hidden="true"><rect x="1" y="1" width="62" height="42" rx="1.5" fill="#FFFFFF" stroke="#10396F"/><path d="M1.5 1.8L32 25L62.5 1.8" fill="none" stroke="#10396F"/><path d="M1.5 42.2L25 21M62.5 42.2L39 21" fill="none" stroke="#10396F" stroke-opacity=".3"/><g class="kf-env-seal"><rect x="26.5" y="19.5" width="11" height="11" fill="#10396F"/><rect x="28.1" y="21.1" width="7.8" height="7.8" fill="none" stroke="#FBFAF6" stroke-width=".6"/></g></svg></span>`;

  /* ---------- shared pieces ---------- */
  const strip = (key) => `<div class="kf-strip">${designsOf(key)
    .map((d) => `<figure>${d.i ? `<img src="${esc(d.i)}" alt="${esc(d.n)}" loading="lazy" width="72" height="90">` : ''}<span class="kf-label kf-muted" style="letter-spacing:.12em">Level ${d.l}</span></figure>`)
    .join('')}</div>`;

  // price promise (Black Friday 2026, section setting): the gold scalloped seal of kk-promise-seal.liquid, a line on
  // The Collection and a note under the kits. The note is the merchant's rich text, so it goes in as HTML.
  const pseal = (w) => `<svg class="kk-pseal" style="width:${w}px" viewBox="-34 -34 68 68" aria-hidden="true" focusable="false"><circle class="kk-pseal__f" r="26"/><circle class="kk-pseal__s" r="26"/><text class="kk-pseal__t" y="-2.5">PRICE</text><text class="kk-pseal__t" y="8.5">PROMISE</text></svg>`;
  const promiseNote = () => {
    const p = cfg.promise;
    return p && p.note ? `<p class="kf-promise-note">${pseal(36)}<span>${p.note}</span></p>` : '';
  };

  // step 1: the four kits as a carousel, The Collection first as the best value
  const kitCarousel = (sel) => {
    // the product page's own kit sits next to The Collection, so both show without scrolling
    const here = THEMES.includes(cfg.currentKit) ? [cfg.currentKit] : [];
    const keys = ORDER.filter((k) => k === 'collection').concat(here, THEMES.filter((k) => !here.includes(k)));
    return `<div class="kf-carousel" role="radiogroup" aria-label="Kit">${keys
      .map((k) => {
        const kk = KITS[k];
        const all = k === 'collection';
        const was = kk.compare && kk.compare > kk.price;
        const on = sel === k;
        return `<button type="button" role="radio" aria-checked="${on}" class="kf-kcard${all ? ' kf-kcard--deal' : ''}${on ? ' is-on' : ''}" data-a="kit" data-v="${k}">
          ${kk.photo ? `<img class="kf-arch" src="${esc(kk.photo)}" alt="">` : '<span class="kf-arch"></span>'}
          <span class="kf-kcard-name">${all ? 'The Collection' : esc(kk.short)}</span>
          <span class="kf-kcard-sub">${all ? `All 3 kits · ${designsOf(k).length} designs` : `${designsOf(k).length} designs`}</span>
          <span class="kf-kcard-price"><span class="kf-price">${money(kk.price)}</span>${was ? `<span class="kf-strike">${money(kk.compare)}</span>` : ''}${all ? `<span class="kf-kcard-save"><span>Best value</span>${was ? `<span>Save ${money(kk.compare - kk.price)}</span>` : ''}</span>` : ''}</span>
          ${all && cfg.promise && cfg.promise.card ? `<span class="kf-promise-card">${pseal(30)}<span>${esc(cfg.promise.card)}</span></span>` : ''}
          ${on ? `<span class="kf-kcard-check">${svg(I.check, 13)}</span>` : ''}</button>`;
      })
      .join('')}</div>`;
  };

  // step 1: every design in the selected kit as a carousel, easiest first (The Collection: theme by theme,
  // with chips that jump to each theme). Tap one to zoom.
  const designPanel = (key, browse) => {
    // browse: no kit picked yet, so all 39 designs show theme by theme
    const all = key === 'collection';
    const item = (k, d, i) => `<figure class="kf-ditem"${all && i === 0 ? ` data-theme="${k}"` : ''}><button type="button" class="kf-dthumb" data-a="zoom" data-v="${k}|${i}" aria-label="See ${esc(d.n)}, level ${d.l}">${d.i ? `<img src="${esc(d.i)}" alt="" loading="lazy" width="100" height="125">` : ''}<span class="kf-dlv" aria-hidden="true">L${d.l}</span></button><figcaption>${esc(d.n)}</figcaption></figure>`;
    return `<section class="kf-designs" aria-label="${browse ? 'Designs in the kits' : `Designs in ${esc(kitName(key))}`}">
      <div class="kf-dhead"><span class="kf-label">${designsOf(key).length} designs ${browse ? 'across the kits' : 'inside'}</span><span class="kf-cap" style="font-size:13px;line-height:1.3">Tap to zoom</span></div>
      ${all ? `<div class="kf-djump">${THEMES.map((t) => `<button type="button" class="kf-chip kf-chip-xs" data-a="dgo" data-v="${t}">${esc(KITS[t].short)} · ${designsOf(t).length}</button>`).join('')}</div>` : ''}
      <div class="kf-dcar" data-kit="${key}">${(all ? THEMES : [key]).map((g) => designsOf(g).map((d, i) => item(g, d, i)).join('')).join('')}</div></section>`;
  };

  const zoomSheet = () => {
    const [key, n] = String(S.zoom).split('|');
    const list = designsOf(key);
    if (!list.length) return '';
    const i = Math.max(0, Math.min(list.length - 1, +n || 0));
    const d = list[i];
    const big = d.i ? d.i.replace(/([?&]width=)\d+/, '$1720') : '';
    const nav = (j, label, icon) => `<button type="button" class="kf-iconbtn kf-iconbtn-sq" data-a="zoom" data-v="${key}|${(j + list.length) % list.length}" aria-label="${label}">${svg(icon, 18)}</button>`;
    return `<div class="kf-sheet" data-a="unzoom"><div class="kf-zoom kf-in" data-a="noop" role="dialog" aria-modal="true" aria-label="${esc(d.n)}">
      <div class="kf-zoom-top"><span class="kf-label kf-muted">${esc(kitName(key))} · ${i + 1} of ${list.length}</span><button type="button" class="kf-iconbtn" data-a="unzoom" aria-label="Close">${svg(I.close)}</button></div>
      ${big ? `<img src="${esc(big)}" alt="${esc(d.n)}">` : ''}
      <div class="kf-zoom-bar">${nav(i - 1, 'Previous design', I.back)}<span style="flex:1;min-width:0;display:flex;flex-direction:column;align-items:center;text-align:center"><span class="kf-strong" style="font-size:17px;line-height:1.3">${esc(d.n)}</span><span class="kf-cap">Level ${d.l}${LEVELS[d.l - 1] ? ` · ${LEVELS[d.l - 1]}` : ''}${d.p ? ` · ${d.p} pieces` : ''}</span></span>${nav(i + 1, 'Next design', I.arrow)}</div>
    </div></div>`;
  };

  // step 4: tapping a name opens this sheet, so the kit choice comes right away
  const pickSheet = () => {
    const [mode, ref] = String(S.pick).split('|');
    const p = mode === 'id' ? S.people.find((x) => String(x.id) === ref) : null;
    if (mode === 'id' && !p) return '';
    const same = p ? 0 : S.people.filter((x) => x.rel === ref).length;
    const label = p ? p.label : same ? `${ref} ${same + 1}` : ref;
    return `<div class="kf-sheet" data-a="unpick"><div class="kf-sheet-in kf-pick kf-in" data-a="noop" role="dialog" aria-modal="true" aria-label="Pick a kit for ${esc(label)}">
      <div style="display:flex;align-items:center;gap:10px">${svg(I.gift, 20)}<h2 class="kf-title" style="flex:1;min-width:0;font-size:28px">${p ? 'Change the kit for' : 'Pick a kit for'} ${esc(label)}</h2>
        <button type="button" class="kf-iconbtn" style="margin-right:-10px" data-a="unpick" aria-label="Close">${svg(I.close)}</button></div>
      <p class="kf-cap" style="margin-top:-8px">Extra-kit price, only with this order. Each one comes in its own box.</p>
      <div class="kf-carousel kf-pick-car" role="radiogroup" aria-label="Kit for ${esc(label)}">${ORDER.filter((k) => k === 'collection').concat(THEMES)
        .map((k) => {
          // same cards as step 1, The Collection first, each with its extra-kit price and saving
          const kk = KITS[k];
          const all = k === 'collection';
          const now = extraPrice(kk.price);
          const saved = kk.price - now;
          const off = Math.round((saved / kk.price) * 100);
          const on = !!p && p.theme === k;
          return `<button type="button" role="radio" aria-checked="${on}" class="kf-kcard${all ? ' kf-kcard--deal' : ''}${on ? ' is-on' : ''}" data-a="pick" data-v="${k}">
            ${kk.photo ? `<img class="kf-arch" src="${esc(kk.photo)}" alt="">` : '<span class="kf-arch"></span>'}
            ${saved && !all ? `<span class="kf-pick-off">${off}% off</span>` : ''}
            <span class="kf-kcard-name">${all ? 'The Collection' : esc(kk.short)}</span>
            <span class="kf-kcard-sub">${all ? `All 3 kits · ${designsOf(k).length} designs` : `${designsOf(k).length} designs`}</span>
            <span class="kf-kcard-price"><span class="kf-price">${money(now)}</span>${saved ? `<span class="kf-strike">${money(kk.price)}</span>` : ''}${saved && all ? `<span class="kf-kcard-save">Save ${money(saved)}</span>` : ''}</span>
            ${saved && !all ? `<span class="kf-pick-save">You save ${money(saved)}</span>` : ''}
            ${on ? `<span class="kf-kcard-check">${svg(I.check, 13)}</span>` : ''}</button>`;
        })
        .join('')}</div>
      ${p && !['Mom', 'Dad', 'Partner'].includes(p.rel) ? `<button type="button" class="kf-textbtn" style="align-self:center" data-a="add" data-v="${esc(p.rel)}">Add another ${esc(p.rel)}</button>` : ''}
    </div></div>`;
  };

  const howItWorks = (title) => {
    const ph = (cfg.photos && cfg.photos.how) || [];
    const steps = ['Follow the grey line on the back', 'Lift out every grey piece', 'Flip it: the clean side is your papercut'];
    return `${title ? `<div style="display:flex;flex-direction:column;gap:4px"><span class="kf-label kf-muted">No drawing · no art skills</span><p class="kf-strong" style="font-size:18px;line-height:1.35">If you can follow a line, you can do this.</p></div>` : ''}
      <ol class="kf-how">${steps
        .map((t, i) => `<li>${ph[i] ? `<img src="${esc(ph[i])}" alt="" loading="lazy">` : ''}<span style="display:flex;gap:6px;align-items:flex-start"><span class="kf-num">${i + 1}</span><span style="font-size:15px;line-height:1.4">${t}</span></span></li>`)
        .join('')}</ol>`;
  };

  const levelPath = (key, exp) => {
    const often = exp === 'often';
    const rows = [
      `<li><span class="kf-node"><i class="${often ? '' : 'on'}"></i><b></b></span><span class="kf-path-body">
        <span style="display:flex;align-items:center;justify-content:space-between;gap:8px"><span class="kf-label">Warm-up</span>${often ? '' : '<span class="kf-tag">Start here</span>'}</span>
        <span class="kf-strong" style="font-size:17px;line-height:1.35">Practice templates</span>
        <span class="kf-cap">Lines, curves and circles to warm up.</span></span></li>`,
    ];
    const ds = designsOf(key);
    for (let L = 1; L <= 5; L++) {
      const at = ds.filter((d) => d.l === L);
      if (!at.length) continue;
      const lo = at[0].p;
      const hi = at[at.length - 1].p;
      const pieces = lo === hi ? `${fmt(lo)} pieces` : `${fmt(lo)}–${fmt(hi)} pieces`;
      const names = at.slice(0, 3).map((d) => d.n).join(', ') + (at.length > 3 ? ` and ${at.length - 3} more` : '');
      const mark = often && L === 3;
      rows.push(`<li><span class="kf-node"><i class="${(L === 1 && !often) || mark ? 'on' : ''}"></i>${L < 5 ? '<b></b>' : ''}</span><span class="kf-path-body">
        <span style="display:flex;align-items:center;justify-content:space-between;gap:8px"><span class="kf-label">Level ${L} · ${LEVELS[L - 1]}</span>${mark ? '<span class="kf-tag">Jump in here</span>' : ''}</span>
        <span class="kf-strong" style="font-size:17px;line-height:1.35">${at.length} ${at.length === 1 ? 'design' : 'designs'} · ${pieces}</span>
        <span class="kf-cap">${esc(names)}</span>
        <span class="kf-path-thumbs">${at.slice(0, 4).map((d) => (d.i ? `<img src="${esc(d.i)}" alt="${esc(d.n)}" loading="lazy">` : '')).join('')}${at.length > 4 ? `<span class="kf-cap" style="padding-left:4px">+${at.length - 4}</span>` : ''}</span></span></li>`);
    }
    return `<ol class="kf-path">${rows.join('')}</ol>`;
  };

  const boxContents = (key, noNote) => {
    const all = key === 'collection';
    const n = designsOf(key).length;
    const items = [
      [I.sheet, `${n} printed designs`, all ? 'Three themes, Levels 1–5' : 'Levels 1–5, lines on the back'],
      [I.practice, 'Practice templates', 'Lines, curves, circles to warm up'],
      [I.knife, 'Craft knife', 'Pen-style, with a safety cap'],
      [I.blades, '10 spare blades', 'In their own case'],
      [I.mat, 'Cutting mat', 'Self-healing, 9×12 in'],
      [I.cards, '3 backing cards', 'Blue, black, green · 8×10 in'],
      [I.guide, 'Illustrated guide', 'From the first cut to the frame'],
    ];
    const box = cfg.photos && cfg.photos.box;
    return `${box ? `<img class="kf-box-photo" src="${esc(box)}" alt="Everything in the box laid out: the mailer box, cutting mat, printed sheets, craft knife, spare blades, guide and three backing cards" loading="lazy">` : ''}
      <ul class="kf-items">${items
        .map(([ic, t, d]) => `<li>${svg(ic, 20)}<span style="display:flex;flex-direction:column;gap:1px;min-width:0"><span class="kf-strong" style="font-size:16px;line-height:1.3">${t}</span><span class="kf-cap" style="font-size:13px;line-height:1.4">${d}</span></span></li>`)
        .join('')}</ul>
      ${noNote ? '' : `<p class="kf-note">${svg(I.check, 18)}<span>${all ? 'One set of tools for all three themes. Nothing else to buy.' : 'Everything you need is in the box. Nothing else to buy.'}</span></p>`}`;
  };

  const coupon = () => {
    const cheap = cheapest();
    const line = `Every kit you add is ${money(offPrice(cheap))} instead of ${money(cheap)}.`;
    return `<div class="kf-coupon" role="note" aria-label="Gift offer: every extra kit is 30% off"><div class="kf-coupon-row">
      <span class="kf-half kf-half--pct" aria-hidden="true">30%</span>
      <span style="flex:1;min-width:0;display:flex;flex-direction:column;gap:4px"><span class="kf-label" style="color:#C9D5E8;white-space:normal">${promoKick()}Gift offer</span><span class="kf-coupon-title">Off</span><span style="font-size:16px;line-height:1.4">${line}</span></span>
      ${cfg.sealWhiteSvg ? `<span class="kf-coupon-seal" aria-hidden="true">${cfg.sealWhiteSvg}</span>` : cfg.sealWhite ? `<img src="${esc(cfg.sealWhite)}" alt="">` : ''}</div></div>`;
  };

  const giftList = () => {
    const o = order();
    const rows = S.people
      .map((p, i) => {
        const k = o.kits[i + 1];
        if (arm === 'steps') {
          return `<li class="kf-panel kf-prow${p.id > S.seenPid ? ' kf-in' : ''}">
          <button type="button" class="kf-prow-main" data-a="repick" data-v="${p.id}" aria-label="For ${esc(p.label)}: ${esc(kitName(p.theme))}. Change the kit">
            ${photo(p.theme) ? `<img class="kf-arch" src="${esc(photo(p.theme))}" alt="">` : ''}
            <span style="flex:1;min-width:0;display:flex;flex-direction:column;gap:1px"><span class="kf-strong" style="font-size:17px;line-height:1.3">For ${esc(p.label)}</span><span class="kf-cap">${esc(kitName(p.theme))} · <span class="kf-prow-change">Change</span></span></span>
            <span class="kf-price" style="display:flex;gap:6px;align-items:baseline">${k.half ? `<span class="kf-strike" style="font-size:15px">${money(k.price)}</span>` : ''}<span>${money(k.final)}</span></span></button>
          <button type="button" class="kf-iconbtn" style="width:36px;height:36px" data-a="rm" data-v="${p.id}" aria-label="Remove ${esc(p.label)}">${svg(I.close, 16)}</button></li>`;
        }
        return `<li class="kf-panel kf-person${p.id > S.seenPid ? ' kf-in' : ''}">
        <div style="display:flex;align-items:center;gap:8px">${svg(I.gift, 18)}<span class="kf-strong" style="flex:1;min-width:0">For ${esc(p.label)}</span>
          <span class="kf-price" style="display:flex;gap:6px;align-items:baseline">${k.half ? `<span class="kf-strike" style="font-size:15px">${money(k.price)}</span>` : ''}<span>${money(k.final)}</span></span>
          <button type="button" class="kf-iconbtn" style="width:36px;height:36px" data-a="rm" data-v="${p.id}" aria-label="Remove ${esc(p.label)}">${svg(I.close, 16)}</button></div>
        <div class="kf-person-themes" role="radiogroup" aria-label="Kit for ${esc(p.label)}">${THEMES.map((t) => `<button type="button" role="radio" aria-checked="${p.theme === t}" class="kf-opt${p.theme === t ? ' is-on' : ''}" data-a="theme" data-v="${p.id}|${t}">${photo(t) ? `<img class="kf-arch" src="${esc(photo(t))}" alt="">` : ''}<span style="font-size:13px;font-weight:500">${esc(KITS[t].short)}</span></button>`).join('')}</div></li>`;
      })
      .join('');
    S.seenPid = S.people.reduce((m, p) => Math.max(m, p.id), S.seenPid);
    const n = S.people.length;
    return `${coupon()}
      <div style="display:flex;flex-direction:column;gap:8px"><span class="kf-label kf-muted">Add someone</span>
        <div style="display:flex;flex-wrap:wrap;gap:6px">${RELS.map((r) => {
          // steps: someone already added shows as a filled chip with a check; tapping it reopens their kit sheet
          const mine = arm === 'steps' ? S.people.filter((p) => p.rel === r) : [];
          return mine.length
            ? `<button type="button" class="kf-chip kf-chip-sm is-added" aria-pressed="true" aria-label="${esc(r)} added. Change the kit" data-a="repick" data-v="${mine[mine.length - 1].id}">${svg(I.check, 14)}${r}${mine.length > 1 ? ` ×${mine.length}` : ''}</button>`
            : `<button type="button" class="kf-chip kf-chip-sm" data-a="add" data-v="${r}">${svg(I.plus, 14)}${r}</button>`;
        }).join('')}</div></div>
      ${n ? `<ul style="display:flex;flex-direction:column;gap:10px">${rows}</ul>
        <p class="kf-save"><span style="display:flex;align-items:center;gap:8px">${svg(I.check, 18)}${o.save > 0 ? `You save ${money(o.save)}` : 'Added to your order'}</span><span style="font-weight:500;font-size:15px">${n === 1 ? '1 more kit, own box' : `${n} more kits, own boxes`}</span></p>`
        : '<p class="kf-cap" style="padding:14px;border:1px dashed rgb(16 57 111 / .45);text-align:center">Tap a name above to add a gift.</p>'}`;
  };

  // compact: the envelope stack sits small on the right of the heading (step 5 test, arm b)
  const mysteryBlock = (compact) => {
    if (!mystery) return '';
    const R = [-6, 3, -2, 5, -4, 2, -5, 4, -1, 6, -3, 1];
    const shown = Math.min(S.mq, compact ? 8 : 12);
    let pile = '';
    if (!S.mq && compact) pile = '<span class="kf-q">?</span>';
    else if (!S.mq) pile = '<span style="position:absolute;top:16px;left:calc(50% - 48px);width:96px;height:66px;border:1px dashed rgb(16 57 111 / .55);display:flex;align-items:center;justify-content:center;font-family:var(--kf-display);font-size:36px;color:var(--kf-muted)">?</span>';
    for (let i = 0; i < shown; i++) {
      const left = compact ? `${shown === 1 ? 35 : Math.round((70 * i) / (shown - 1))}px` : shown === 1 ? 'calc(50% - 48px)' : `calc((100% - 96px) * ${(i / (shown - 1)).toFixed(3)})`;
      pile += envelope(i >= S.shownMq ? 'is-new' : '', `left:${left};transform:rotate(${R[i]}deg);z-index:${i + 1}`);
    }
    if (S.mq > shown) pile += `<span class="kf-tag" style="position:absolute;right:0;top:0">+${S.mq - shown}</span>`;
    S.shownMq = S.mq;
    const picks = [1, 3, 5, 10]
      .map((n) => `<button type="button" class="kf-pick" aria-pressed="${S.mq === n}" data-a="mq" data-v="${n}" aria-label="${n} mystery prints, ${money(n * mystery.price)}"><b>${n}</b><small>${money(n * mystery.price)}</small></button>`)
      .join('');
    const pileBox = `<div class="kf-pile${compact ? ' kf-pile--sm' : ''}" aria-live="polite" aria-label="${S.mq} envelopes in your stack">${pile}</div>`;
    return `<div class="kf-mystery-head${compact ? ' kf-mystery-head--sm' : ''}">${mystery.image ? `<img src="${esc(mystery.image)}" alt="Hands holding sealed envelopes">` : ''}
        <span style="display:flex;flex-direction:column;gap:4px;min-width:0;flex:1"><span class="kf-label kf-muted">Series 01 · sealed</span><span class="kf-title">Mystery prints</span><span class="kf-price">${money(mystery.price)} each</span></span>${compact ? pileBox : ''}</div>
      <p class="kf-body-s">${esc(cfg.mysteryText || 'One printed design in a sealed envelope, picked at random. Any level from 1 to 5.')}</p>
      ${compact ? '' : pileBox}
      <div style="display:flex;flex-direction:column;gap:8px"><span class="kf-label kf-muted">Quick add</span><div class="kf-picks">${picks}</div></div>
      <div style="display:flex;align-items:center;justify-content:space-between;gap:12px">
        <div class="kf-stepper" role="group" aria-label="Mystery prints"><button type="button" data-a="mq-" aria-label="One less">${svg(I.minus, 16)}</button><output>${S.mq}</output><button type="button" data-a="mq+" aria-label="One more">${svg(I.plus, 16)}</button></div>
        <span style="text-align:right;display:flex;flex-direction:column"><span class="kf-price" style="font-size:18px">${money(S.mq * mystery.price)}</span><span class="kf-cap">${S.mq === 1 ? '1 sealed envelope' : `${S.mq} sealed envelopes`}</span></span></div>`;
  };

  // doubles the blades: one tap for 2 packs (60 blades), tap again for 1
  const bladesX2 = () => `<button type="button" class="kf-chip kf-chip-sm kf-x2" aria-pressed="${S.blades === 2}" data-a="blades-x2">${S.blades === 2 ? `${svg(I.check, 14)}${blades.count * 2} blades · ${money(blades.price * 2)}` : `×2 · ${blades.count * 2} blades for ${money(blades.price * 2)}`}</button>`;
  const bladesRow = (withToggle) => {
    if (!blades) return '';
    return `<div class="kf-panel kf-addon">${blades.image ? `<img src="${esc(blades.image)}" alt="A clear case of spare craft-knife blades">` : svg(I.blades, 40)}
      <span style="flex:1;min-width:0;display:flex;flex-direction:column;gap:2px"><span class="kf-strong" style="font-size:17px">Spare blades, ${blades.count} · ${money(blades.price)}</span>
      <span class="kf-cap">The kit has 10. A fresh blade is the secret to clean cuts: swap as soon as it starts to drag.</span>${withToggle && S.blades ? bladesX2() : ''}</span>
      ${withToggle ? `<button type="button" class="kf-chip kf-chip-sm" aria-pressed="${!!S.blades}" data-a="blades">${S.blades ? 'Added' : 'Add'}</button>` : ''}</div>`;
  };

  // step 5 test, arm b: the blades as a direct question, like the chat asks it
  const bladesAsk = () => {
    if (!blades) return '';
    return `<div class="kf-panel kf-ask">
      <div class="kf-mystery-head kf-mystery-head--sm">${blades.image ? `<img src="${esc(blades.image)}" alt="A clear case of spare craft-knife blades">` : svg(I.blades, 40)}
        <span style="display:flex;flex-direction:column;gap:4px;min-width:0;flex:1"><span class="kf-title">Add spare blades?</span><span class="kf-price">${blades.count} blades · ${money(blades.price)}</span></span></div>
      <p class="kf-body-s">The kit comes with 10. A fresh blade is the secret to clean cuts: swap as soon as it starts to drag.</p>
      <div class="kf-ask-btns" role="group" aria-label="Spare blades">
        <button type="button" class="kf-btn" aria-pressed="${!!S.blades}" data-a="blades-yes">${S.blades ? `${svg(I.check, 16)}Added` : `Yes, add ${blades.count}`}</button>
        <button type="button" class="kf-btn-line${S.bladesNo ? ' is-on' : ''}" aria-pressed="${!!S.bladesNo}" data-a="blades-no">No, 10 is enough</button></div>${S.blades ? bladesX2() : ''}</div>`;
  };

  const kitPicker = (act, sel) => {
    const themes = THEMES.map((k) => {
      const kk = KITS[k];
      return `<button type="button" role="radio" aria-checked="${sel === k}" class="kf-opt kf-tile${sel === k ? ' is-on' : ''}" data-a="${act}" data-v="${k}">
        ${kk.photo ? `<img class="kf-arch" src="${esc(kk.photo)}" alt="">` : '<span class="kf-arch" style="display:block;height:104px"></span>'}
        <span style="font-size:15px;font-weight:700;line-height:1.2">${esc(kk.short)}</span><span class="kf-price" style="font-size:15px">${money(kk.price)}</span></button>`;
    }).join('');
    let deal = '';
    const c = KITS.collection;
    if (c) {
      const save = c.compare && c.compare > c.price ? c.compare - c.price : 0;
      deal = `<button type="button" role="radio" aria-checked="${sel === 'collection'}" class="kf-opt kf-deal${sel === 'collection' ? ' is-on' : ''}" data-a="${act}" data-v="collection">
        <span class="kf-deal-ribbon">Best value${save ? ` · save ${money(save)}` : ''}</span>
        <span class="kf-deal-body">${c.photo ? `<img class="kf-arch" src="${esc(c.photo)}" alt="">` : ''}
          <span style="flex:1;min-width:0;display:flex;flex-direction:column;gap:3px">
            <span class="kf-label kf-muted">Three kits, one box</span>
            <span class="kf-title" style="font-size:28px">The Collection</span>
            <span class="kf-cap">${designsOf('collection').length} designs · one set of tools</span>
            <span style="display:flex;gap:8px;align-items:baseline"><span class="kf-price" style="font-size:22px">${money(c.price)}</span>${save ? `<span class="kf-strike" style="font-size:16px">${money(c.compare)}</span>` : ''}</span>
          </span>
          ${sel === 'collection' ? `<span class="kf-deal-check">${svg(I.check, 14)}</span>` : ''}
        </span></button>`;
    }
    return `<div role="radiogroup" aria-label="Kit" style="display:flex;flex-direction:column;gap:8px"><div class="kf-tiles">${themes}</div>${deal}</div>`;
  };

  const orderLines = (o) => {
    const rows = o.kits.map((k) => ({ img: photo(k.key), title: kitName(k.key), sub: k.half ? `${k.who} · ${k.price - k.final === Math.round(k.price * SECOND_OFF) ? '30% off' : `${money(k.price - k.final)} off`}` : k.who, half: k.half, full: money(k.price), price: money(k.final) }));
    if (S.mq && mystery) rows.push({ img: mystery.image, title: 'Mystery prints', sub: S.mq === 1 ? '1 sealed envelope' : `${S.mq} sealed envelopes`, price: money(o.mysteryValue) });
    if (S.blades && blades) rows.push({ img: blades.image, title: 'Spare blades', sub: `${blades.count * S.blades} blades`, price: money(o.bladesValue) });
    return `<ul class="kf-lines">${rows
      .map((r) => `<li>${r.img ? `<img class="kf-arch" src="${esc(r.img)}" alt="">` : ''}<span style="flex:1;min-width:0;display:flex;flex-direction:column;gap:1px"><span class="kf-strong" style="font-size:17px">${esc(r.title)}</span><span class="kf-cap">${esc(r.sub)}</span></span>
        <span class="kf-price kf-line-price"><span>${r.price}</span>${r.half ? `<span class="kf-strike" style="font-size:13px">${r.full}</span>` : ''}</span></li>`)
      .join('')}</ul>`;
  };

  const goingSheet = () => {
    const o = order();
    const bits = [o.kits.length === 1 ? '1 kit' : `${o.kits.length} kits`];
    if (S.mq) bits.push(S.mq === 1 ? '1 mystery print' : `${S.mq} mystery prints`);
    if (S.blades && blades) bits.push(`${blades.count * S.blades} spare blades`);
    const line = (bits.length > 1 ? `${bits.slice(0, -1).join(', ')} and ${bits[bits.length - 1]}` : bits[0]) + `, ${money(o.total)}.`;
    return `<div class="kf-sheet"><div class="kf-sheet-in kf-in" role="status">
      ${cfg.seal ? `<img class="kf-stamp" src="${esc(cfg.seal)}" alt="" style="width:56px;height:56px">` : ''}
      <h2 class="kf-title">${S.error ? 'Something went wrong' : 'Taking you to checkout'}</h2>
      <p class="kf-body-s ${S.error ? 'kf-error' : 'kf-muted'}">${S.error ? esc(S.error) : esc(line)}</p>
      ${S.error ? '<button type="button" class="kf-btn kf-block" data-a="checkout">Try again</button><button type="button" class="kf-textbtn" data-a="back-order">Back to my order</button>' : '<span class="kf-typing kf-dots" aria-hidden="true"><i></i><i></i><i></i></span>'}</div></div>`;
  };

  /* ---------- A · guided steps ---------- */
  const NAMES = ['Kit', 'Box', 'Levels', 'Gifts', 'Extras', 'Review'];
  const STEP_IDS = ['kit', 'box', 'levels', 'gifts', 'extras', 'review'];
  const NEXT = ["Next: what's in the box", 'Next: made for first-timers', 'Next: a gift offer', 'Next: the extras', 'Next: review', ''];

  const stepBody = (o) => {
    const gift = S.who !== 'me';
    switch (S.step) {
      case 0:
        return `<div><h2 class="kf-title">Choose your kit</h2><p class="kf-body-s kf-muted" style="margin-top:4px">No art skills needed. Just follow the lines.</p>${S.needKit && !S.kit ? '<p class="kf-error kf-need" role="alert">Pick a kit to continue.</p>' : ''}${kitCarousel(S.kit)}${promiseNote()}</div>
          ${designPanel(S.kit || 'collection', !S.kit)}`;
      case 1:
        return `<div style="display:flex;flex-direction:column;gap:4px"><h2 class="kf-title">What's in the box</h2><p class="kf-body-s kf-muted">${S.kit === 'collection' ? 'All three themes, one set of tools.' : 'Everything in one box. Nothing else to buy.'}</p></div>${boxContents(S.kit, true)}`;
      case 2:
        return `<div style="display:flex;flex-direction:column;gap:4px"><h2 class="kf-title">Made for first-timers</h2><p class="kf-body-s kf-muted">If you can follow a line, you can do this.</p></div>
          ${howItWorks(false)}
          ${gift ? `<p class="kf-note">${svg(I.gift, 18)}<span>Never cut paper before? Start with the practice templates.</span></p>` : ''}
          <hr class="kf-rule"><div style="display:flex;flex-direction:column;gap:4px"><h3 class="kf-title" style="font-size:30px">Your path through the kit</h3><p class="kf-cap">Start easy, then go up one level at a time.</p></div>
          ${levelPath(S.kit, 'never')}`;
      case 3:
        return `<div style="display:flex;flex-direction:column;gap:4px"><h2 class="kf-title">Extra kits, 30% off</h2><p class="kf-body-s kf-muted">${gift ? 'Tap a name, then pick their kit. Each one comes in its own box.' : 'Kits make easy gifts. Tap a name, then pick their kit.'}</p></div>
          ${S.who === 'me' && S.kit !== 'collection' && KITS.collection ? `<div class="kf-panel kf-addon">${photo('collection') ? `<img class="kf-arch" src="${esc(photo('collection'))}" alt="">` : ''}<span style="flex:1;min-width:0;display:flex;flex-direction:column;gap:2px"><span class="kf-strong" style="font-size:16px">Or all three themes for you</span><span class="kf-cap">The Collection: ${designsOf('collection').length} designs in one box, ${money(KITS.collection.price)}.</span></span><button type="button" class="kf-chip kf-chip-sm" data-a="collection">+${money(KITS.collection.price - KITS[S.kit].price)}</button></div>` : ''}
          ${giftList()}`;
      case 4:
        return `<div style="display:flex;flex-direction:column;gap:4px"><h2 class="kf-title">A few extras</h2><p class="kf-body-s kf-muted">They go in the same shipment. Skip them if you like.</p></div>
          ${x5On && x5 === 'b'
            ? `${bladesAsk()}${mystery ? `<div class="kf-panel" style="padding:16px;display:flex;flex-direction:column;gap:12px">${mysteryBlock(true)}</div>` : ''}`
            : `${mystery ? `<div class="kf-panel" style="padding:16px;display:flex;flex-direction:column;gap:12px">${mysteryBlock()}</div>` : ''}${bladesRow(true)}`}`;
      default:
        return `<div style="display:flex;flex-direction:column;gap:4px"><h2 class="kf-title">Your order</h2></div>
          ${orderLines(o)}
          <div style="display:flex;flex-direction:column;gap:6px">
            ${o.save > 0 ? `<p class="kf-sum-row" style="color:var(--kf-ok);font-weight:700"><span>Extra-kit savings</span><span class="kf-price">−${money(o.save)}</span></p>` : ''}
            <p class="kf-sum-row" style="font-size:20px"><span class="kf-strong">Total</span><span class="kf-price kf-strong">${money(o.total)}</span></p>
            <p class="kf-cap">Shipping and taxes at checkout.</p></div>
          <div style="display:flex;flex-wrap:wrap;gap:4px 16px"><button type="button" class="kf-textbtn" data-a="go" data-v="0">Change kit</button><button type="button" class="kf-textbtn" data-a="go" data-v="3">Change gifts</button><button type="button" class="kf-textbtn" data-a="go" data-v="4">Change extras</button></div>
          ${cfg.cutoff ? `<p class="kf-note">${svg(I.cal, 18)}<span>${esc(cfg.cutoff)}</span></p>` : ''}`;
    }
  };

  const renderSteps = () => {
    const o = order();
    const step = S.step;
    const scroller = app.querySelector('.kf-scroll');
    const keep = scroller && !S.enter ? scroller.scrollTop : 0;
    const car0 = app.querySelector('.kf-carousel');
    const keepX = car0 && !S.enter ? car0.scrollLeft : null;
    const dc0 = app.querySelector('.kf-dcar');
    const keepD = dc0 && !S.enter && dc0.dataset.kit === (S.kit || 'collection') ? dc0.scrollLeft : null;
    const noKit = !S.kit;
    let next = 'Next step';
    if (step === 3 && !S.people.length) next = 'Skip gifts';
    if (step === 4) next = S.blades || S.mq ? 'Review' : 'Skip extras';
    if (step === 5) next = 'Checkout';
    let foot = NEXT[step];
    if (o.save > 0 && step >= 3) foot = `You save ${money(o.save)} on extra kits`;
    if (step === 5 && !o.save) foot = 'Shipping at checkout';
    if (noKit) foot = 'Choose a kit to continue';
    app.innerHTML = `<div class="kf-top"><div class="kf-top-row">
        <button type="button" class="kf-iconbtn" data-a="close" aria-label="Close">${svg(I.close)}</button>
        ${cfg.logo ? `<img class="kf-logo" src="${esc(cfg.logo)}" alt="Kikirie">` : '<span class="kf-label">Kikirie</span>'}
        ${step < 5 && !noKit ? '<button type="button" class="kf-textbtn" style="min-width:44px" data-a="go" data-v="5">Review</button>' : '<span style="width:44px"></span>'}</div>
        <div class="kf-bars">${NAMES.map((n, i) => `<button type="button" class="kf-bar ${i < step ? 'done' : i === step ? 'now' : ''}" aria-label="Step ${i + 1}: ${n}"${i > step ? ' disabled' : ''} data-a="go" data-v="${i}"></button>`).join('')}</div>
        <div style="display:flex;justify-content:space-between;padding:8px 8px 0"><span class="kf-label">Step ${step + 1} of 6</span><span class="kf-label kf-muted">${NAMES[step]}</span></div></div>
      <div class="kf-scroll"><div class="kf-body${S.enter ? ' kf-in' : ''}">${stepBody(o)}</div></div>
      <div class="kf-foot"><div style="flex:1;min-width:0;display:flex;flex-direction:column"><span class="kf-price kf-total">${noKit ? `<span class="kf-from">From</span> ${money(cheapest())}` : money(o.total)}</span><span class="kf-cap kf-ellipsis">${foot}</span></div>
        ${step > 0 ? `<button type="button" class="kf-iconbtn kf-iconbtn-sq" data-a="back" aria-label="Back">${svg(I.back, 18)}</button>` : ''}
        <button type="button" class="kf-btn${noKit ? ' kf-btn--wait' : ''}"${noKit ? ' aria-disabled="true"' : ''} data-a="${step === 5 ? 'checkout' : 'next'}">${next}${svg(I.arrow, 16)}</button></div>
      ${S.going ? goingSheet() : ''}${S.zoom && step === 0 ? zoomSheet() : ''}${S.pick && step === 3 ? pickSheet() : ''}`;
    const sc = app.querySelector('.kf-scroll');
    if (sc) sc.scrollTop = keep;
    const dc = app.querySelector('.kf-dcar');
    if (dc && keepD != null) dc.scrollLeft = keepD;
    const car = app.querySelector('.kf-carousel');
    if (car) {
      if (keepX != null) car.scrollLeft = keepX;
      const on = car.querySelector('.is-on');
      if (on) {
        const cr = car.getBoundingClientRect();
        const r = on.getBoundingClientRect();
        // snap the selected card to the start (the browser clamps at the end), so scroll-snap keeps it there
        if (r.right > cr.right - 16 || r.left < cr.left + 16) car.scrollLeft += r.left - cr.left - 16;
      }
    }
    if (step === 3 && !promoFx4) {
      const cp = app.querySelector('.kf-coupon');
      if (cp && window.KikiriePromo && window.KikiriePromo.kicker && window.KikiriePromo.kicker()) { promoFx4 = true; window.KikiriePromo.pieces(cp); }
    }
    S.enter = false;
    stepEvent(`steps:${STEP_IDS[step]}`);
  };

  /* ---------- B · chat ---------- */
  const EXP = { never: 'Never', few: 'A few times', often: 'Often', unsure: 'Not sure' };
  const beats = () => {
    const c = S.c;
    const they = S.who === 'gift';
    const you = they ? 'they' : 'you';
    const picked = c.kitPicked || S.kit;
    const T = (text) => ({ t: 'text', text });
    const C = (card) => ({ t: 'card', card });
    const hard = hardest(S.kit);
    const b = [];
    b.push({ id: 'who', bot: [T(`Hi, I'm ${cfg.guide || 'Kiki'}. I'll help you choose a paper cutting kit. It takes about a minute.`), T('Who are you shopping for?')], input: 'who', answer: S.mainFor ? `A gift for ${S.mainFor}` : { gift: 'A gift', me: 'Myself', both: 'Both' }[S.who] });
    b.push({ id: 'kit', bot: [T(they ? 'Lovely. What would they love to cut?' : S.who === 'both' ? "Let's start with yours. What would you like to cut?" : 'What would you like to cut first?')], input: 'kit', answer: kitName(picked), answerImg: photo(picked) });
    const n = designsOf(picked).length;
    const galleryLine = picked === 'collection' ? `The Collection has all ${n} designs: Florals, Animals and Landscapes.` : `The ${kitName(picked)} has ${n} designs, ${LINES[picked] || 'from Level 1 to Level 5'}.`;
    b.push({ id: 'exp', bot: [T(galleryLine), C('gallery'), T(they ? 'Have they ever cut paper with a craft knife?' : 'Have you ever cut paper with a craft knife?')], input: 'exp', answer: EXP[c.exp] });
    const re = {
      never: `Perfect, the kit is made for exactly that. No drawing and no art skills: every line is printed on the back of the paper, ${you} just follow it.`,
      few: 'Good, the hand remembers fast. Every line is printed on the back of the paper, so there is nothing to draw.',
      often: hard ? `Then Levels 4 and 5 are for ${they ? 'them' : 'you'}: the ${hard.n} has ${fmt(hard.p)} pieces. Every line is printed on the back, so it is all cutting.` : 'Then Levels 4 and 5 are for you. Every line is printed on the back, so it is all cutting.',
      unsure: 'No problem. The kit starts with practice templates and Level 1, made for people who have never cut paper. Every line is printed on the back, nothing to draw.',
    }[c.exp];
    b.push({ id: 'path', bot: [T(re), C('how'), T('Here is the path through the kit. Practice templates first, then one level at a time.'), C('path')], input: 'toBox', answer: "What's in the box?" });
    b.push({ id: 'box', bot: [T(they ? 'Everything they need is in one box. Nothing else to buy.' : 'Everything you need is in one box. Nothing else to buy.'), C('box')], input: 'toMore', answer: 'Looks good' });
    if (S.who === 'me' && picked !== 'collection' && KITS.collection) {
      const all = KITS.collection;
      b.push({ id: 'upgrade', bot: [T(`Want more to cut? The Collection has all ${designsOf('collection').length} designs and one set of tools: ${money(all.price)}${all.compare ? ` instead of ${money(all.compare)} for three kits` : ''}.`)], input: 'upgrade', answer: S.kit === 'collection' ? 'Switch to The Collection' : `Keep the ${kitName(picked)}` });
    }
    const np = S.people.length;
    b.push({ id: 'gifts', bot: [T('Before the extras, a good one: every extra kit is 30% off.'), T(S.who === 'me' ? 'Kits make easy gifts. Anyone you would like to surprise? Each kit comes in its own box.' : `${S.who === 'both' ? 'Who else' : 'Anyone else'} on your list? Each kit comes in its own box.`)], input: 'gifts', answer: np ? `${S.people.map((p) => p.label).join(', ')}: ${np === 1 ? '1 more kit' : `${np} more kits`}` : 'Just this one' });
    if (mystery) b.push({ id: 'mystery', bot: [T('Now my favorite part.'), T(`Mystery prints: one design in a sealed envelope, picked at random. ${money(mystery.price)} each. How many should I slip in?`)], input: 'mystery', answer: S.mq ? (S.mq === 1 ? '1 mystery print' : `${S.mq} mystery prints`) : 'No mystery prints' });
    if (blades) b.push({ id: 'blades', bot: [T('Last one: shall I add spare blades? A fresh blade is the secret to clean cuts.')], input: 'blades', answer: S.blades ? `Add ${blades.count * S.blades} blades` : 'No blades, thanks' });
    b.push({ id: 'summary', bot: [T('Here is everything. You can still change the extras.'), C('summary')], input: '' });
    return b;
  };

  let chatTimer = null;
  const reveal = () => {
    clearTimeout(chatTimer);
    const b = beats()[S.c.stage];
    if (!b) return;
    if (S.c.shown >= b.bot.length) {
      if (S.c.typing) {
        S.c.typing = false;
        render();
      }
      stepEvent(`chat:${b.id}`);
      return;
    }
    const m = b.bot[S.c.shown];
    const wait = m.t === 'text' ? Math.min(1700, 650 + m.text.length * 7) : 700;
    if (!S.c.typing) {
      S.c.typing = true;
      render();
    }
    chatTimer = setTimeout(() => {
      S.c.shown += 1;
      render();
      reveal();
    }, wait);
  };
  const answer = (patch) => {
    clearTimeout(chatTimer);
    Object.assign(S, patch.s || {});
    Object.assign(S.c, patch.c || {});
    S.c.stage += 1;
    S.c.shown = 0;
    S.c.typing = true;
    render();
    reveal();
  };
  const editAt = (i) => {
    clearTimeout(chatTimer);
    const b = beats()[i];
    if (!b) return;
    S.c.stage = i;
    S.c.shown = b.bot.length;
    S.c.typing = false;
    S.going = false;
    render();
  };

  const hash = (s) => {
    let h = 5381;
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
  };
  const ava = () => `<span class="kf-ava">${cfg.seal ? `<img src="${esc(cfg.seal)}" alt="">` : ''}</span>`;
  const card = (inner) => `<div class="kf-row"><span class="kf-gap"></span><div class="kf-card">${inner}</div></div>`;

  const feedItems = () => {
    const items = [];
    const all = beats();
    const picked = S.c.kitPicked || S.kit;
    all.forEach((b, i) => {
      if (i > S.c.stage) return;
      const count = i < S.c.stage ? b.bot.length : S.c.shown;
      let first = true;
      b.bot.slice(0, count).forEach((m, j) => {
        if (m.card === 'summary') return;
        let html = '';
        if (m.t === 'text') {
          html = `<div class="kf-row">${first ? ava() : '<span class="kf-gap"></span>'}<p class="kf-bub">${esc(m.text)}</p></div>`;
          first = false;
        } else if (m.card === 'gallery') {
          html = card(`<div style="display:flex;flex-direction:column;gap:8px"><span class="kf-label kf-muted">${designsOf(picked).length} designs · easiest first</span>${strip(picked)}</div>`);
        } else if (m.card === 'how') {
          html = card(`<div style="display:flex;flex-direction:column;gap:14px">${howItWorks(true)}</div>`);
        } else if (m.card === 'path') {
          html = card(levelPath(S.kit, S.c.exp));
        } else if (m.card === 'box') {
          html = card(`<div style="display:flex;flex-direction:column;gap:14px">${boxContents(S.kit)}</div>`);
        }
        items.push({ key: `${b.id}:${j}:${hash(html)}`, html: `<div style="display:flex;flex-direction:column;gap:8px;padding-top:${j === 0 ? 8 : 0}px">${html}</div>`, anchor: i === S.c.stage && j === 0 });
      });
      if (i < S.c.stage && b.answer) {
        const html = `<div class="kf-me" style="padding-top:4px"><button type="button" class="kf-edit" data-a="edit" data-v="${i}" aria-label="Change this answer">${svg(I.edit, 16)}</button><span class="kf-me-bub">${b.answerImg ? `<img src="${esc(b.answerImg)}" alt="">` : ''}${esc(b.answer)}</span></div>`;
        items.push({ key: `${b.id}:a:${hash(html)}`, html, anchor: false });
      }
    });
    return items;
  };

  const reconcile = (box, items) => {
    const have = new Map();
    Array.from(box.children).forEach((el) => have.set(el.dataset.key, el));
    let prev = null;
    items.forEach((it) => {
      let el = have.get(it.key);
      if (el) have.delete(it.key);
      else {
        const t = document.createElement('template');
        t.innerHTML = it.html.trim();
        el = t.content.firstElementChild;
        el.dataset.key = it.key;
        el.classList.add('kf-in');
      }
      if (it.anchor) el.setAttribute('data-anchor', '');
      else el.removeAttribute('data-anchor');
      const ref = prev ? prev.nextSibling : box.firstChild;
      if (el !== ref) box.insertBefore(el, ref);
      prev = el;
    });
    have.forEach((el) => el.remove());
  };

  const chatTail = (o) => {
    const b = beats()[S.c.stage];
    const open = b && S.c.shown >= b.bot.length && !S.c.typing;
    const fin = S.c.animStage !== S.c.stage ? ' kf-in' : '';
    if (open) S.c.animStage = S.c.stage;
    let html = '';
    if (b && b.id === 'summary' && S.c.shown >= 2) {
      html += `<div class="kf-row${fin}"><span class="kf-gap"></span><div class="kf-card" style="display:flex;flex-direction:column;gap:12px">
        <span class="kf-label kf-muted">Your order</span>${orderLines(o)}
        ${mystery ? `<div style="display:flex;gap:10px;align-items:center"><span style="flex:1" class="kf-strong">Mystery prints</span><div class="kf-stepper" role="group" aria-label="Mystery prints" style="height:38px"><button type="button" style="width:34px;height:36px" data-a="mq-" aria-label="One less">${svg(I.minus, 14)}</button><output style="min-width:22px;font-size:16px">${S.mq}</output><button type="button" style="width:34px;height:36px" data-a="mq+" aria-label="One more">${svg(I.plus, 14)}</button></div></div>` : ''}
        ${blades ? `<div style="display:flex;gap:10px;align-items:center"><span style="flex:1" class="kf-strong">Spare blades, ${blades.count}</span><button type="button" class="kf-chip kf-chip-sm" aria-pressed="${!!S.blades}" data-a="blades">${S.blades ? 'Added' : `Add · ${money(blades.price)}`}</button></div>` : ''}
        ${o.save > 0 ? `<p class="kf-sum-row" style="color:var(--kf-ok);font-weight:700;font-size:16px"><span>Extra-kit savings</span><span class="kf-price">−${money(o.save)}</span></p>` : ''}
        <p class="kf-sum-row"><span class="kf-strong">Total</span><span class="kf-price kf-strong">${money(o.total)}</span></p>
        ${S.going && !S.error ? `<div style="display:flex;align-items:center;gap:12px;padding:10px 12px;background:var(--kf-mist)">${cfg.seal ? `<img class="kf-stamp" src="${esc(cfg.seal)}" alt="" style="width:40px;height:40px">` : ''}<span class="kf-strong" style="flex:1">Taking you to checkout</span><span class="kf-typing kf-dots" aria-hidden="true"><i></i><i></i><i></i></span></div>`
          : `<button type="button" class="kf-btn kf-block" data-a="checkout">Checkout · ${money(o.total)}</button>${S.error ? `<p class="kf-error">${esc(S.error)}</p>` : ''}${cfg.cutoff ? `<p class="kf-cap">${esc(cfg.cutoff)}</p>` : ''}`}
        </div></div>`;
      if (S.going && !S.error) html += `<div class="kf-row kf-in">${ava()}<p class="kf-bub">Lovely. Taking you to secure checkout now. Happy cutting.</p></div>`;
    }
    if (S.c.typing) html += `<div class="kf-row"><span class="kf-gap"></span><span class="kf-typing" aria-label="${esc(cfg.guide || 'Kiki')} is typing"><i></i><i></i><i></i></span></div>`;
    if (!open) return html;
    const chips = (list) => `<div class="kf-replies${fin}">${list.map(([a, v, l]) => `<button type="button" class="kf-chip" data-a="${a}" data-v="${v}">${esc(l)}</button>`).join('')}</div>`;
    switch (b.input) {
      case 'who':
        html += chips([['c-who', 'gift', 'A gift'], ['c-who', 'me', 'Myself'], ['c-who', 'both', 'Both']]);
        break;
      case 'kit':
        html += `<div class="kf-widget${fin}">${kitPicker('c-kit', null)}</div>`;
        break;
      case 'exp': {
        const l = [['c-exp', 'never', 'Never'], ['c-exp', 'few', 'A few times'], ['c-exp', 'often', 'Often']];
        if (S.who === 'gift') l.push(['c-exp', 'unsure', 'Not sure']);
        html += chips(l);
        break;
      }
      case 'toBox':
        html += chips([['c-next', '', "What's in the box?"]]);
        break;
      case 'toMore':
        html += chips([['c-next', '', 'Looks good']]);
        break;
      case 'upgrade':
        html += chips([['c-up', 'collection', `Switch to The Collection · +${money(KITS.collection.price - KITS[S.c.kitPicked || S.kit].price)}`], ['c-up', 'keep', `Keep the ${kitName(S.c.kitPicked || S.kit)}`]]);
        break;
      case 'gifts':
        html += `<div class="kf-widget${fin}">${giftList()}${S.people.length ? `<button type="button" class="kf-btn kf-block" data-a="c-gifts">Add ${S.people.length === 1 ? '1 kit' : `${S.people.length} kits`} · +${money(o.gifts)}</button>` : '<button type="button" class="kf-btn-line kf-block" data-a="c-gifts">Continue without</button>'}</div>`;
        break;
      case 'mystery':
        html += `<div class="kf-widget${fin}"><div class="kf-panel" style="padding:14px;display:flex;flex-direction:column;gap:12px">${mysteryBlock()}</div>${S.mq ? `<button type="button" class="kf-btn kf-block" data-a="c-mq">Add ${S.mq} · ${money(o.mysteryValue)}</button>` : '<button type="button" class="kf-btn-line kf-block" data-a="c-mq">Continue without</button>'}</div>`;
        break;
      case 'blades':
        html += `<div class="kf-widget${fin}">${bladesRow(false)}<div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px"><button type="button" class="kf-btn-line" data-a="c-blades" data-v="no">No thanks</button><button type="button" class="kf-btn" data-a="c-blades" data-v="yes">Add · ${money(blades.price)}</button></div></div>`;
        break;
      default:
        break;
    }
    return html;
  };

  const renderChat = () => {
    const o = order();
    if (!app.querySelector('.kf-chat-feed')) {
      app.innerHTML = `<div class="kf-top" style="padding:0;background:var(--kf-surface)" data-kf-head></div>
        <div class="kf-scroll" data-kf-scroll><div class="kf-chat-feed"><div data-kf-feed style="display:flex;flex-direction:column;gap:8px"></div><div data-kf-tail style="display:flex;flex-direction:column;gap:8px"></div></div></div>`;
    }
    const head = app.querySelector('[data-kf-head]');
    const hasKit = S.c.stage > 1;
    const live = [kitName(S.kit)];
    if (S.people.length) live.push(S.people.length === 1 ? '1 gift' : `${S.people.length} gifts`);
    if (S.mq) live.push(`${S.mq} mystery`);
    if (S.blades) live.push('blades');
    head.innerHTML = `<div style="display:flex;align-items:center;gap:8px;padding:6px 12px 6px 6px">
        <button type="button" class="kf-iconbtn" data-a="close" aria-label="Close">${svg(I.close)}</button>
        <span class="kf-ava" style="width:38px;height:38px">${cfg.seal ? `<img src="${esc(cfg.seal)}" alt="" style="width:24px;height:24px">` : ''}</span>
        <span style="flex:1;min-width:0;display:flex;flex-direction:column"><span class="kf-strong" style="font-size:17px;line-height:1.3">${esc(cfg.guide || 'Kiki')}</span><span class="kf-cap" style="line-height:1.3">Your Kikirie kit guide</span></span>
        ${hasKit ? `<span class="kf-tag" style="font-size:15px;letter-spacing:.04em;padding:6px 10px">${svg(I.bag, 14)}${money(o.total)}</span>` : ''}</div>
      ${hasKit ? `<div class="kf-live">${photo(S.kit) ? `<img class="kf-arch" src="${esc(photo(S.kit))}" alt="">` : ''}<span class="kf-label kf-ellipsis" style="flex:1;min-width:0;letter-spacing:.14em">${esc(live.join(' · '))}</span><span class="kf-label kf-muted" style="letter-spacing:.14em">Live</span></div>` : ''}`;
    reconcile(app.querySelector('[data-kf-feed]'), feedItems());
    app.querySelector('[data-kf-tail]').innerHTML = chatTail(o);
    const sheet = app.querySelector('.kf-sheet');
    if (sheet) sheet.remove();
    // scroll: down to the newest content, never past the first message of the current question
    const sc = app.querySelector('[data-kf-scroll]');
    const anchor = app.querySelector('[data-kf-feed] [data-anchor]');
    const max = sc.scrollHeight - sc.clientHeight;
    const target = anchor && !S.going ? Math.min(max, anchor.offsetTop - 12) : max;
    if (target > sc.scrollTop + 4) {
      const smooth = document.visibilityState === 'visible' && !(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
      if (smooth) sc.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
      else sc.scrollTop = Math.max(0, target);
    }
  };

  const render = () => (arm === 'chat' ? renderChat() : renderSteps());

  /* ---------- checkout ---------- */
  const checkout = async () => {
    if (S.going && !S.error) return;
    S.going = true;
    S.error = '';
    render();
    const props = { _kk_flow: arm };
    const items = [{ id: KITS[S.kit].variant, quantity: 1, properties: S.mainFor ? { 'Gift for': S.mainFor, _kk_flow: arm } : props }];
    S.people.forEach((p) => items.push({ id: KITS[p.theme].variant, quantity: 1, properties: { 'Gift for': p.label, _kk_flow: arm } }));
    if (mystery && S.mq) items.push({ id: mystery.variant, quantity: S.mq, properties: props });
    if (blades && S.blades) items.push({ id: blades.variant, quantity: S.blades, properties: props });
    try {
      // a second run of the flow replaces the lines of the first one instead of doubling them
      const cur = await nativeFetch(`${cfg.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const updates = {};
      (cur.items || []).forEach((it) => { if (it.properties && it.properties._kk_flow) updates[it.key] = 0; });
      if (Object.keys(updates).length) {
        await nativeFetch(`${cfg.routes.update}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates }) });
      }
      const res = await nativeFetch(`${cfg.routes.add}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.description || json.message || 'The kits could not be added.');
      await tagCart({ ab_who: S.who });
      const o = order();
      publish('ab_add_to_cart', {
        value: Math.round(o.total) / 100,
        currency: cfg.currency || 'USD',
        kit: S.kit,
        who: S.who,
        kits: o.kits.length,
        gift_kits: S.people.length,
        mystery: S.mq,
        blades: S.blades ? S.blades : 'no',
        savings: Math.round(o.save) / 100,
        addons_value: Math.round(o.mysteryValue + o.bladesValue) / 100,
      });
      setTimeout(() => { location.href = cfg.routes.checkout || '/checkout'; }, 250);
    } catch (err) {
      S.error = `${err && err.message ? err.message : 'Something went wrong.'} Please try again.`;
      render();
    }
  };

  /* ---------- events inside the dialog ---------- */
  app.addEventListener('click', (e) => {
    const t = e.target.closest('[data-a]');
    if (!t || !app.contains(t)) return;
    const a = t.dataset.a;
    const v = t.dataset.v;
    switch (a) {
      case 'close': dialog.close(); return;
      case 'who': S.who = v; if (v !== 'gift') S.mainFor = null; break;
      case 'kit': S.kit = v; S.needKit = false; break;
      case 'next': if (!S.kit) { S.needKit = true; break; } S.step = Math.min(5, S.step + 1); S.enter = true; break;
      case 'back': S.step = Math.max(0, S.step - 1); S.enter = true; break;
      case 'go': if (!S.kit && +v > 0) { S.needKit = true; break; } S.step = Math.max(0, Math.min(5, +v)); S.enter = true; break;
      case 'collection': S.kit = 'collection'; break;
      case 'add': if (arm === 'steps') S.pick = `new|${v}`; else addPerson(v); break;
      case 'pick': {
        const [mode, ref] = String(S.pick || '').split('|');
        if (mode === 'new') addPerson(ref, v);
        else if (mode === 'id') S.people.forEach((p) => { if (String(p.id) === ref) p.theme = v; });
        break;
      }
      case 'repick': S.pick = `id|${v}`; break;
      case 'unpick': break;
      case 'zoom': S.zoom = v; break;
      case 'unzoom': break;
      case 'dgo': {
        // The Collection: jump the designs carousel to a theme, no re-render
        const dc = app.querySelector('.kf-dcar');
        const el = dc && dc.querySelector(`[data-theme="${v}"]`);
        if (el) dc.scrollTo({ left: el.offsetLeft - dc.firstElementChild.offsetLeft, behavior: 'smooth' });
        return;
      }
      case 'rm': S.people = S.people.filter((p) => String(p.id) !== v); break;
      case 'theme': {
        const [id, key] = v.split('|');
        S.people.forEach((p) => { if (String(p.id) === id) p.theme = key; });
        break;
      }
      case 'mq': S.mq = +v; break;
      case 'mq+': S.mq = Math.min(30, S.mq + 1); break;
      case 'mq-': S.mq = Math.max(0, S.mq - 1); S.shownMq = Math.min(S.shownMq, S.mq); break;
      case 'blades': S.blades = S.blades ? 0 : 1; break;
      case 'blades-yes': S.blades = S.blades || 1; S.bladesNo = false; break;
      case 'blades-no': S.blades = 0; S.bladesNo = true; break;
      case 'blades-x2': S.blades = S.blades === 2 ? 1 : 2; S.bladesNo = false; break;
      case 'checkout': checkout(); return;
      case 'back-order': S.going = false; S.error = ''; break;
      case 'c-who': answer({ s: { who: v, mainFor: null } }); return;
      case 'c-kit': answer({ s: { kit: v }, c: { kitPicked: v } }); return;
      case 'c-exp': answer({ c: { exp: v } }); return;
      case 'c-next': answer({}); return;
      case 'c-up': answer({ s: { kit: v === 'collection' ? 'collection' : S.c.kitPicked || S.kit } }); return;
      case 'c-gifts': answer({}); return;
      case 'c-mq': answer({}); return;
      case 'c-blades': answer({ s: { blades: v === 'yes' ? 1 : 0 } }); return;
      case 'edit': editAt(+v); return;
      default: return;
    }
    if (a !== 'zoom') S.zoom = null;
    if (a !== 'add' && a !== 'repick') S.pick = null;
    if (a === 'mq' || a === 'mq-') S.shownMq = Math.min(S.shownMq, S.mq);
    render();
  });
  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('kkf-lock');
    clearTimeout(chatTimer);
    if (S.c.typing) {
      // finish the pending message instantly so reopening shows it
      const b = beats()[S.c.stage];
      if (b) S.c.shown = b.bot.length;
      S.c.typing = false;
    }
  });

  dialog.addEventListener('cancel', (e) => {
    // Esc closes an open sheet first, then the flow
    if (!S.zoom && !S.pick) return;
    e.preventDefault();
    S.zoom = null;
    S.pick = null;
    render();
  });

  /* ---------- opening ---------- */
  const open = (key, forName, who) => {
    promoFx4 = false;
    if (key && KITS[key] && !KITS.collection) {
      S.kit = key;
      if (arm === 'chat' && S.c.stage <= 1) S.c.kitPicked = null;
    }
    if (who === 'gift' || who === 'me' || who === 'both') {
      // "Who is it for?" chips on the product page: the answer is already given
      S.who = who;
      S.mainFor = who === 'gift' && forName ? String(forName).slice(0, 40) : null;
      if (arm === 'chat' && S.c.stage === 0) {
        S.c.stage = 1;
        S.c.shown = 0;
      }
    }
    publish('ab_open', {});
    if (!dialog.open) {
      try { dialog.showModal(); } catch (err) { dialog.setAttribute('open', ''); }
    }
    document.documentElement.classList.add('kkf-lock');
    S.enter = true;
    lastStep = null;
    render();
    if (arm === 'chat') reveal();
  };

  const TRIGGER = cfg.trigger || 'product-form-component [ref="addToCartButton"], sticky-add-to-cart [ref="addToCartButton"], [data-kk-flow-open]';
  if (arm === 'plain') {
    document.addEventListener('click', (e) => { if (e.target.closest && e.target.closest(TRIGGER)) publish('ab_open', {}); }, true);
    // the plain product page adds with Horizon's own form: report its adds the same way
    window.fetch = function (input) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const p = nativeFetch.apply(this, arguments);
      if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
        p.then((res) => {
          if (!res || !res.ok) return;
          res.clone().json().then((json) => {
            const items = json && json.items ? json.items : json && json.id ? [json] : [];
            const value = items.reduce((t, it) => t + (it.final_line_price != null ? it.final_line_price : (it.price || 0) * (it.quantity || 1)), 0);
            publish('ab_add_to_cart', { value: Math.round(value) / 100, currency: cfg.currency || 'USD', kits: items.reduce((t, it) => t + (it.quantity || 1), 0), gift_kits: 0, mystery: 0, blades: 'no' });
            tagCart();
          }).catch(() => {});
        }).catch(() => {});
      }
      return p;
    };
  } else {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest ? e.target.closest(TRIGGER) : null;
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      open(btn.dataset.kkFlowOpen, btn.dataset.kkFlowFor, btn.dataset.kkFlowWho);
    }, true);
    const label = cfg.buttonLabel || 'Choose your kit';
    const relabel = () => {
      document.querySelectorAll('product-form-component [ref="addToCartButton"], sticky-add-to-cart [ref="addToCartButton"]').forEach((btn) => {
        const walker = document.createTreeWalker(btn, NodeFilter.SHOW_TEXT);
        let node;
        let done = false;
        while ((node = walker.nextNode())) {
          if (!node.nodeValue.trim()) continue;
          if (!done) { node.nodeValue = ` ${label} `; done = true; }
        }
        btn.setAttribute('aria-haspopup', 'dialog');
      });
    };
    relabel();
    setTimeout(relabel, 800);
    try {
      if (new URLSearchParams(location.search).get('flow_open') === '1') open();
    } catch (err) { /* ignore */ }
  }

  window.KikirieFlow = { arm, source, open, publish, config: cfg, state: S };
  publish('ab_view', {});
})();
